import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';
import { SystemSetting } from '../services/types';
import { useAuth } from './AuthContext';

interface SettingsContextType {
    settings: SystemSetting[];
    loading: boolean;
    error: string | null;
    getSetting: (key: string) => string;
    updateSetting: (key: string, value: string) => Promise<void>;
    refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [settings, setSettings] = useState<SystemSetting[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const data = await api.settings.list();
            setSettings(data);
            setError(null);
        } catch (err: any) {
            console.error('Failed to fetch settings', err);
            setError(err.message || 'Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchSettings();
        } else {
            setSettings([]); // Clear settings on logout
        }
    }, [user]);

    const getSetting = (key: string): string => {
        const setting = settings.find(s => s.key === key);
        return setting ? setting.value : '';
    };

    const updateSetting = async (key: string, value: string) => {
        try {
            await api.settings.update(key, { value });
            // Optimistic update or refresh
            setSettings(prev => prev.map(s => s.key === key ? { ...s, value } : s));
        } catch (err: any) {
            console.error('Failed to update setting', err);
            throw err;
        }
    };

    return (
        <SettingsContext.Provider value={{
            settings,
            loading,
            error,
            getSetting,
            updateSetting,
            refreshSettings: fetchSettings
        }}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};
